import React, { useState } from 'react';
import { X, Star } from 'lucide-react';

const ReviewSubmissionModal = ({ 
  book, 
  currentUser,
  onClose, 
  onSubmit,
  AMAZON_MARKETPLACES 
}) => {
  const [step, setStep] = useState(1); // 1: Turn In Assignment, 2: Submit Review Link
  const [formData, setFormData] = useState({
    amazonReviewerName: currentUser?.name || '',
    marketplace: currentUser?.country || 'US',
    rating: 5,
    reviewText: '',
    amazonReviewLink: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleInitialSubmit = () => {
    if (!formData.amazonReviewerName || !formData.reviewText || formData.rating === 0) {
      alert('Please fill in all required fields');
      return;
    }
    
    const wordCount = formData.reviewText.trim().split(/\s+/).filter(w => w).length;
    if (wordCount < 15) {
      alert(`Your review must be at least 15 words. Current: ${wordCount} words`);
      return;
    }
    
    setShowSuccessMessage(true);
  };

  const handleFinalSubmit = async () => {
    if (!formData.amazonReviewLink) {
      alert('Please provide the Amazon review link');
      return;
    }

    

    const wordCount = formData.reviewText.trim().split(/\s+/).filter(w => w).length;
    if (wordCount < 15) {
      alert(`Your review must be at least 15 words. Current: ${wordCount} words`);
      return;
    }

    // Call the parent submit function
    await onSubmit({
      bookId: book.id,
      reviewerId: currentUser.id,
      reviewerName: formData.amazonReviewerName,
      rating: formData.rating,
      reviewText: formData.reviewText,
      amazonReviewLink: formData.amazonReviewLink,
      marketplace: formData.marketplace,
      proofScreenshot: 'Link provided',
      amazonReviewerName: formData.amazonReviewerName,
      wordCount: wordCount
    });

    // Show success alert
    alert('✅ Review submitted successfully!\n\nYour review is now pending admin approval. You will receive your coins once approved.');

    onClose();
  };

  if (showSuccessMessage) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-8">
            <button onClick={onClose} className="float-right text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
            
            <h2 className="text-2xl font-bold mb-6">Your Todos</h2>
            
            <div className="space-y-4 text-gray-700">
              <div className="flex items-start gap-3">
                <div className="text-green-600 text-2xl mt-1">✓</div>
                
              </div>
              
              <p className="leading-relaxed">
                Our automated system will check for your review over the next several days. However, if you'd like to 
                provide us with the direct link to your review to be sure we find it, you can do so by clicking 
                'Submit Review Link' below. It will be sent to our staff for approval.
              </p>
              
              <div className="flex gap-4 mt-6">
                <a
                  href={book.amazonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition font-medium inline-block text-center"
                >
                  VIEW ON AMAZON
                </a>
                <button
                  onClick={() => {
                    setShowSuccessMessage(false);
                    setStep(2);
                  }}
                  className="bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition font-medium"
                >
                  SUBMIT REVIEW LINK
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-8">
            <button onClick={onClose} className="float-right text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
            
            <h2 className="text-3xl font-bold text-center mb-6">Submit a Review Link</h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                In order to directly submit your review, it must be visible on Amazon!
              </p>
              
              <p className="text-gray-700">
                If you see your review on Amazon, please follow these steps to send us your review's direct link:
              </p>
              
              <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
                <li>Find your review on Amazon</li>
                <li>Right click the bold title of your review</li>
                <li>Click 'copy link address'</li>
                <li>Paste the link to your review from Amazon below and we'll go take a look and see if we can verify everything for you!</li>
              </ol>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Direct Amazon Review Link:
                </label>
                <input
                  type="url"
                  value={formData.amazonReviewLink}
                  onChange={(e) => setFormData(prev => ({ ...prev, amazonReviewLink: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="https://www.amazon.com/gp/customer-reviews/..."
                />
              </div>
              
              {formData.amazonReviewLink && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-6 h-6 ${star <= formData.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                      />
                    ))}
                    <span className="text-gray-700 font-medium ml-2">{formData.amazonReviewerName}</span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Content
                    </label>
                    <textarea
                      value={formData.reviewText}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white"
                      rows="4"
                    />
                  </div>
                </div>
              )}
              
              <button
                onClick={handleFinalSubmit}
                disabled={!formData.amazonReviewLink}
                className="w-full bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition font-medium text-lg"
              >
                SUBMIT
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Turn In Assignment
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <button onClick={onClose} className="float-right text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
          
          <h2 className="text-3xl font-bold text-center mb-8">Submit Your Review</h2>
          
          <div className="space-y-6">
            {/* Amazon Reviewer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Amazon Reviewer Name
              </label>
              <input
                type="text"
                value={formData.amazonReviewerName}
                onChange={(e) => setFormData(prev => ({ ...prev, amazonReviewerName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Your name as shown on Amazon"
              />
              <p className="text-sm text-gray-600 mt-2">
                Please provide the Amazon Reviewer Name that you posted the review with.
                All of your reviews must be made under the same Reviewer Name.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                To find your Reviewer Name, visit{' '}
                <a 
                  href="https://www.amazon.com/gp/profile" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  https://www.amazon.com/gp/profile
                </a>{' '}
                while signed in to Amazon to see your Reviewer Name on the page in large text.
              </p>
            </div>

            {/* Country/Marketplace Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country Where You Posted Review
              </label>
              <select
                value={formData.marketplace}
                onChange={(e) => setFormData(prev => ({ ...prev, marketplace: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {Object.entries(AMAZON_MARKETPLACES || {
                  US: { name: 'United States', flag: '🇺🇸' },
                  UK: { name: 'United Kingdom', flag: '🇬🇧' },
                  CA: { name: 'Canada', flag: '🇨🇦' },
                  DE: { name: 'Germany', flag: '🇩🇪' },
                  FR: { name: 'France', flag: '🇫🇷' }
                }).map(([code, info]) => (
                  <option key={code} value={code}>
                    {info.flag} {info.name}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-600 mt-2">
                Please select the country's Amazon page where you have posted your review.
              </p>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Rating
              </label>
              <div className="flex gap-2 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 ${star <= formData.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Please provide the number of stars you rated this book in your Amazon review.
              </p>
            </div>

            {/* Review Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Content
              </label>
              <textarea
                value={formData.reviewText}
                onChange={(e) => setFormData(prev => ({ ...prev, reviewText: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows="6"
                placeholder="Paste your review text here..."
              />
              <p className="text-sm text-gray-600 mt-2">
                Please paste the content of your review here to make it easier to find!
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Word count: {formData.reviewText.trim().split(/\s+/).filter(w => w).length}{' '}
                {formData.reviewText.trim().split(/\s+/).filter(w => w).length < 15 ? 
                  <span className="text-red-600">(minimum 15 words required)</span> : 
                  <span className="text-green-600">✓</span>
                }
              </p>
            </div>

            <button
              onClick={handleInitialSubmit}
              className="w-full bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 transition font-medium text-lg"
            >
              SUBMIT REVIEW
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionModal;